import { NumberBlockV1 } from '@frontend/api-sms-number-block';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { NakedButton, useTooltip } from '@frontend/design-system';

const iconButtonStyle = {
  height: 'var(--fabric-number-n-7-px)',
  width: 'var(--fabric-number-n-7-px)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--fabric-border-radius-small, 4px)',
};

const blockedIconButtonStyle = {
  background: 'var(--fabric-color-neutral-10)',
  ':hover': {
    background: 'var(--fabric-color-neutral-20)',
  },
};

type BlockedIndicatorInlineProps =
  | {
      /** Phone number to check blocked status for */
      phoneNumber: string;
      /** Location ID for the blocked check */
      locationId: string;
      /** Override tooltip label */
      tooltipLabel?: string;
      /** When true, skip the API check and always show the indicator */
      show?: never;
    }
  | {
      /** When true, skip the API check and always show the indicator */
      show: true;
      /** Override tooltip label */
      tooltipLabel?: string;
      phoneNumber?: never;
      locationId?: never;
    };

/**
 * Inline blocked indicator icon with tooltip.
 *
 * Two usage modes:
 * 1. **Auto-detect** (default): Pass `phoneNumber` + `locationId` — component queries
 *    blocked status and conditionally renders.
 * 2. **Explicit**: Pass `show={true}` — always renders without any API call.
 *    Useful when the parent already knows blocked state (e.g. group thread header).
 */
export const BlockedIndicatorInline = (props: BlockedIndicatorInlineProps) => {
  const { t } = useTranslation('thread-header');
  const { tooltipLabel } = props;

  // Auto-detect mode: query blocked status
  const shouldQuery = !props.show && !!props.phoneNumber && !!props.locationId;
  const normalizedPhone = shouldQuery ? formatPhoneNumberE164(props.phoneNumber!) : '';

  const { data: blockedData } = NumberBlockV1.Queries.useIsBlockedQuery({
    request: {
      personPhone: normalizedPhone,
      locationId: shouldQuery ? props.locationId! : '',
    },
    options: {
      enabled: shouldQuery,
    },
  });

  const isBlocked = props.show || blockedData?.isBlocked;

  const { tooltipProps, triggerProps, Tooltip } = useTooltip({ placement: 'top', hoverDelay: 300 });

  if (!isBlocked) {
    return null;
  }

  return (
    <>
      <NakedButton css={[iconButtonStyle, blockedIconButtonStyle]} aria-label={t('Contact blocked')} {...triggerProps}>
        <Icon name='block' size={16} />
      </NakedButton>
      <Tooltip {...tooltipProps}>{tooltipLabel ?? t('This contact has been blocked.')}</Tooltip>
    </>
  );
};
